import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAdminStats, getAdminFunnel, getAdminClients, getAdminTestimonials, getEmailStats, getClientEmailHistory } from '../utils/api';
import { C } from '../utils/constants';
import { useSequenceEmails, storedEmailCopy, NO_DESCRIPTION } from './admin/sequenceEmails';
import { EMAIL_PROBLEM, emailSendStatusOfRow, problemBadgeStyle } from './admin/emailSendStatus';

// Each email's name and trigger description come from where they are stored
// (ry_sequence_emails, via ./admin/sequenceEmails.js) — the same source as the
// Email Stats tab, and no copy in code. An email with nothing stored shows its
// raw key as the name and "No description set" as the description.
const seqLabelFor = (copy, seq) => {
  const stored = storedEmailCopy(copy, seq);
  return {
    name: stored.name || seq,
    trigger: stored.trigger || (copy.ready ? NO_DESCRIPTION : null),
  };
};

const StatCard = ({ label, value, sub, color }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    padding: '20px 22px',
    border: `1.5px solid ${C.border}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  }}>
    <div style={{
      fontSize: '0.7rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: C.terracotta,
      marginBottom: 8,
      fontWeight: 600,
    }}>
      {label}
    </div>
    <div style={{
      fontFamily: "'Forum', serif",
      fontSize: '2.2rem',
      color: color || C.brown,
      lineHeight: 1,
      marginBottom: 4,
    }}>
      {value ?? '—'}
    </div>
    {sub && (
      <div style={{ fontSize: '0.78rem', color: C.terracotta, marginTop: 4 }}>
        {sub}
      </div>
    )}
  </div>
);

export const Admin = () => {
  const emailCopy = useSequenceEmails();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [clients, setClients] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [emailStats, setEmailStats] = useState([]);
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientEmailHistory, setClientEmailHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user?.isAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }
    Promise.all([
      getAdminStats(),
      getAdminFunnel(),
      getAdminClients(),
      getAdminTestimonials(),
      getEmailStats(),
    ])
      .then(([s, f, c, t, e]) => {
        setStats(s.data);
        const fd = f.data;
        const flat = { 'Onboarding': fd.onboardingComplete };
        (fd.days || []).forEach(d => { flat[`Day ${d.dayNum}`] = d.completed; });
        setFunnel(flat);
        setClients(c.data || []);
        setTestimonials(t.data || []);
        setEmailStats(e.data || []);
      })
      .catch(() => setError('Failed to load admin data.'))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const toggleClientHistory = async (clientId) => {
    if (expandedClientId === clientId) {
      setExpandedClientId(null);
      return;
    }
    setExpandedClientId(clientId);
    if (clientEmailHistory[clientId]) return; // already loaded
    setLoadingHistory(clientId);
    try {
      const res = await getClientEmailHistory(clientId);
      setClientEmailHistory(prev => ({ ...prev, [clientId]: res.data || [] }));
    } catch {
      setClientEmailHistory(prev => ({ ...prev, [clientId]: [] }));
    } finally {
      setLoadingHistory(null);
    }
  };

  const fmtSeq = (seq) => seq
    .replace('sequence_', 'Seq ')
    .replace(/_email_(\d+)$/, ' · Email $1')
    .replace(/_([a-z])$/, (_, l) => ' · ' + l.toUpperCase())
    .replace(/_/g, ' ');

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#1a0800', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: "'Forum', serif", color: C.gold, fontSize: '1.2rem' }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <p style={{ color: C.terracotta }}>{error}</p>
        <button className="btn-secondary" onClick={() => navigate('/dashboard')}>← Back to Portal</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f0eb' }}>
      {/* Admin nav */}
      <div style={{
        background: `linear-gradient(135deg, #0a0400 0%, ${C.darkBrown} 100%)`,
        padding: '0 24px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 16px rgba(0,0,0,0.4)',
      }}>
        <span style={{
          fontFamily: "'Forum', serif",
          color: '#fff',
          fontSize: '1.15rem',
          letterSpacing: '0.06em',
        }}>
          Admin Dashboard
        </span>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.8)',
            padding: '8px 18px',
            borderRadius: 7,
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontFamily: "'Forum', serif",
            letterSpacing: '0.04em',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
        >
          ← Back to Portal
        </button>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px 80px' }}>
        {/* Stat cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}>
          <StatCard label="Total Clients" value={stats?.totalEnrolled} color={C.brown} />
          <StatCard
            label="Completed Program"
            value={stats?.totalCompleted}
            sub={stats?.totalEnrolled ? `${Math.round((stats.totalCompleted / stats.totalEnrolled) * 100)}% completion rate` : undefined}
            color={C.amber}
          />
          <StatCard label="Sessions Booked" value={stats?.callsBooked} color={C.gold} />
          <StatCard label="Active This Week" value={stats?.activeThisWeek} color={C.terracotta} />
        </div>

        {/* 2-column: funnel + insights */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20,
          marginBottom: 24,
        }}>
          {/* Funnel */}
          <div style={{
            background: '#fff',
            borderRadius: 14,
            padding: '24px',
            border: `1.5px solid ${C.border}`,
          }}>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '1.1rem',
              marginBottom: 20,
            }}>
              Progress Funnel
            </h2>
            {funnel ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {Object.entries(funnel).map(([stage, count], i, arr) => {
                  // Scale every bar against the LARGEST count so the biggest is
                  // 100% and no bar can exceed its container. (Previously the
                  // denominator was arr[0][1] — the FIRST row's count, i.e.
                  // Onboarding — so any later row with a higher count, e.g. Day 1
                  // (7) vs Onboarding (6), computed >100% and overflowed.)
                  const maxVal = Math.max(...arr.map(e => e[1] || 0), 1);
                  const pct = Math.min(100, maxVal > 0 ? (count / maxVal) * 100 : 0);
                  return (
                    <div key={stage}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.82rem', color: C.text, textTransform: 'capitalize' }}>
                          {stage.replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.brown }}>{count}</span>
                      </div>
                      <div style={{ height: 6, background: C.veryLightBrown, borderRadius: 3 }}>
                        <div style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${C.amber}, ${C.gold})`,
                          borderRadius: 3,
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: C.terracotta, fontSize: '0.85rem' }}>No funnel data available.</p>
            )}
          </div>

          {/* Key insights */}
          <div style={{
            background: '#fff',
            borderRadius: 14,
            padding: '24px',
            border: `1.5px solid ${C.border}`,
          }}>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '1.1rem',
              marginBottom: 20,
            }}>
              Key Insights
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                {
                  icon: '📈',
                  label: 'Total Enrolled',
                  value: stats?.totalEnrolled ?? '—',
                },
                {
                  icon: '📅',
                  label: 'Sessions Booked',
                  value: stats?.callsBooked ?? '—',
                },
                {
                  icon: '✅',
                  label: 'Completed Program',
                  value: stats?.totalCompleted ?? '—',
                },
              ].map(item => (
                <div key={item.label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '12px 16px',
                  background: C.veryLightBrown,
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: '1.4rem' }}>{item.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', color: C.terracotta, marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: '1.2rem' }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Auto-email notice */}
        <div style={{
          background: `linear-gradient(135deg, ${C.darkBrown}12, ${C.brown}08)`,
          border: `1.5px solid ${C.border}`,
          borderRadius: 12,
          padding: '16px 22px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}>
          <span style={{ fontSize: '1.3rem' }}>✉️</span>
          <div>
            <div style={{ fontWeight: 700, color: C.brown, fontSize: '0.9rem', marginBottom: 2 }}>
              Auto-Email Sequences Active
            </div>
            <div style={{ fontSize: '0.8rem', color: C.terracotta }}>
              Clients who book a session receive an automated confirmation email. Re-engagement emails fire 48hrs after last activity.
            </div>
          </div>
        </div>

        {/* Email Sequence Overview */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: `1.5px solid ${C.border}`,
          overflow: 'hidden',
          marginBottom: 24,
        }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
            <h2 style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: '1.1rem', margin: 0 }}>
              Email Sequence Overview
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: C.terracotta }}>
              * Open rates may be understated — some email clients block tracking pixels.
              Click rate is the primary conversion signal.
            </p>
          </div>
          {emailStats.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: C.terracotta, fontSize: '0.85rem' }}>
              No emails sent yet — stats will appear here once sequences start firing.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: C.veryLightBrown }}>
                    {['Sequence', 'Sent', 'Opened', 'Open Rate', 'Clicked', 'Click Rate', 'Bounced'].map(h => (
                      <th key={h} style={{
                        padding: '10px 16px', textAlign: h === 'Sequence' ? 'left' : 'center',
                        color: C.terracotta, fontWeight: 700, fontSize: '0.72rem',
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {emailStats.map((row, i) => (
                    <tr key={row.sequence} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fefdfb' }}>
                      <td style={{ padding: '11px 16px', color: C.brown, fontWeight: 600 }}>
                        {seqLabelFor(emailCopy, row.sequence).name}
                        {seqLabelFor(emailCopy, row.sequence).trigger && (
                          <div style={{ fontSize: '0.68rem', color: C.terracotta, fontWeight: 400, marginTop: 2 }}>
                            {seqLabelFor(emailCopy, row.sequence).trigger}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: C.text }}>{row.sent}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: C.text }}>{row.opened}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                        <span style={{ color: row.openRate >= 40 ? C.amber : C.terracotta, fontWeight: 600 }}>{row.openRate}%</span>
                      </td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: C.text }}>{row.clicked}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                        <span style={{ color: row.clickRate >= 10 ? C.amber : C.terracotta, fontWeight: 600 }}>{row.clickRate}%</span>
                      </td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: row.bounced > 0 ? '#c0392b' : C.text }}>{row.bounced}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Client table */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: `1.5px solid ${C.border}`,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '1.1rem',
            }}>
              All Clients ({clients.length})
            </h2>
          </div>

          {clients.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.terracotta, fontSize: '0.9rem' }}>
              No client data available.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: C.veryLightBrown }}>
                    {['Name', 'Email', 'Days Done', 'Onboarded', 'Session Booked', 'Last Active', 'Joined', 'Emails'].map(h => (
                      <th key={h} style={{
                        padding: '10px 16px',
                        textAlign: 'left',
                        color: C.terracotta,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client, i) => {
                    return <React.Fragment key={client.id || i}>
                    <tr
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: i % 2 === 0 ? '#fff' : '#fefdfb',
                      }}
                    >
                      <td style={{ padding: '12px 16px', color: C.brown, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {[client.firstName, client.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: C.text, whiteSpace: 'nowrap' }}>
                        {client.email || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: client.daysCompleted > 0 ? `${C.gold}22` : C.veryLightBrown,
                          color: client.daysCompleted > 0 ? C.amber : C.terracotta,
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                        }}>
                          {client.daysCompleted ?? 0}/7
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {client.onboardingComplete
                          ? <span style={{ color: C.amber }}>✓</span>
                          : <span style={{ color: C.border }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {client.callBooked
                          ? <span style={{ color: C.amber }}>✓</span>
                          : <span style={{ color: C.border }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', color: C.text, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        {client.lastActive
                          ? new Date(client.lastActive).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: C.text, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        {client.createdAt
                          ? new Date(client.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => toggleClientHistory(client.id)}
                          style={{
                            background: expandedClientId === client.id ? C.brown : 'none',
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: '0.72rem',
                            color: expandedClientId === client.id ? '#fff' : C.terracotta,
                            cursor: 'pointer',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {loadingHistory === client.id ? '…' : expandedClientId === client.id ? 'Hide' : 'Emails'}
                        </button>
                      </td>
                    </tr>
                    {expandedClientId === client.id && (
                      <tr key={`${client.id}-history`}>
                        <td colSpan={8} style={{ padding: 0, background: C.veryLightBrown }}>
                          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}` }}>
                            {!clientEmailHistory[client.id] ? (
                              <p style={{ margin: 0, color: C.terracotta, fontSize: '0.82rem' }}>Loading…</p>
                            ) : clientEmailHistory[client.id].length === 0 ? (
                              <p style={{ margin: 0, color: C.terracotta, fontSize: '0.82rem' }}>No emails sent to this client yet.</p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {clientEmailHistory[client.id].map(ev => {
                                  const opened  = !!ev.opened_at;
                                  const clicked = !!ev.clicked_at;
                                  // What happened to the send: the SAME rule and badge
                                  // as the client timeline (admin/emailSendStatus.js).
                                  const status  = emailSendStatusOfRow(ev);
                                  const problem = EMAIL_PROBLEM[status] || null;
                                  const neverSent = status === 'failed' || status === 'unknown';
                                  return (
                                    <div key={ev.id} style={{
                                      display: 'flex', flexWrap: 'wrap', gap: '8px 20px',
                                      alignItems: 'center',
                                      background: '#fff',
                                      border: `1px solid ${C.border}`,
                                      borderRadius: 8,
                                      padding: '10px 14px',
                                      fontSize: '0.78rem',
                                    }}>
                                      <span style={{ color: C.brown, fontWeight: 700, minWidth: 160 }}>{seqLabelFor(emailCopy, ev.sequence).name}</span>
                                      {problem ? (
                                        <span style={problemBadgeStyle(problem)} title={problem.note}>
                                          ⚠ {problem.label} {fmtDate(ev.created_at)}
                                        </span>
                                      ) : (
                                        <span style={{ color: C.terracotta }}>Sent {fmtDate(ev.created_at)}</span>
                                      )}
                                      {!neverSent && (
                                        <span style={{ color: opened ? C.amber : C.border }}>
                                          {opened ? `✓ Opened ${fmtDate(ev.opened_at)}` : '○ Not opened'}
                                        </span>
                                      )}
                                      {!neverSent && (
                                        <span style={{ color: clicked ? C.amber : C.border }}>
                                          {clicked ? `✓ Clicked ${fmtDate(ev.clicked_at)}` : '○ Not clicked'}
                                        </span>
                                      )}
                                      {clicked && ev.link_clicked && (
                                        <span style={{ color: C.terracotta, fontSize: '0.7rem', fontStyle: 'italic', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          → {ev.link_clicked}
                                        </span>
                                      )}
                                      {problem && (
                                        <span style={{ color: problem.fg, fontSize: '0.7rem', flexBasis: '100%' }}>
                                          {problem.note}{neverSent ? ' (date shown is the attempt)' : ''}.
                                        </span>
                                      )}
                                      {ev.event_type === 'delivered' && !problem && (
                                        <span style={{ color: C.terracotta, fontSize: '0.72rem' }}>✓ Delivered</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Testimonials */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: `1.5px solid ${C.border}`,
          overflow: 'hidden',
          marginTop: 24,
        }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
            <h2 style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: '1.1rem' }}>
              Client Testimonials ({testimonials.length})
            </h2>
          </div>

          {testimonials.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.terracotta, fontSize: '0.9rem' }}>
              No testimonials yet — they'll appear here once clients complete the program.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {testimonials.map((t, i) => {
                const firstName = t.users?.first_name || 'Client';
                const ratings = [t.star_1 || 0, t.star_2 || 0, t.star_3 || 0, t.star_4 || 0, t.star_5 || 0];
                const avgRaw = ratings.reduce((a, b) => a + b, 0) / ratings.length;
                const stars = Math.round(avgRaw);
                const starStr = '★'.repeat(stars) + '☆'.repeat(5 - stars);
                const ratingsLine = ratings.map((r, i) => `Q${i + 1}: ${r}/5`).join(' · ');

                const CopyBtn = ({ text, label }) => {
                  const [copied, setCopied] = useState(false);
                  return (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(text);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      style={{
                        background: copied ? C.amber : 'none',
                        border: `1px solid ${copied ? C.amber : C.border}`,
                        borderRadius: 6,
                        padding: '3px 10px',
                        fontSize: '0.7rem',
                        color: copied ? '#fff' : C.terracotta,
                        cursor: 'pointer',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s',
                      }}
                    >
                      {copied ? '✓ Copied' : label || 'Copy'}
                    </button>
                  );
                };

                return (
                  <div key={t.id} style={{
                    padding: '24px',
                    borderBottom: i < testimonials.length - 1 ? `1px solid ${C.border}` : 'none',
                  }}>
                    {/* Admin info row — separate from shareable content */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: 14, flexWrap: 'wrap', gap: 8,
                      padding: '8px 12px',
                      background: C.veryLightBrown,
                      borderRadius: 8,
                      fontSize: '0.78rem',
                    }}>
                      <span style={{ color: C.terracotta }}>
                        📧 <strong>{t.users?.first_name} {t.users?.last_name}</strong> · {t.users?.email}
                      </span>
                      <span style={{ color: C.terracotta, opacity: 0.7 }}>
                        {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                      </span>
                    </div>

                    {/* Average star rating + per-question breakdown */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ color: C.gold, fontSize: '1.2rem', letterSpacing: 2 }}>{starStr}</span>
                      <span style={{ fontSize: '0.78rem', color: C.terracotta, fontWeight: 700 }}>{avgRaw.toFixed(1)}/5 avg</span>
                      <div style={{ marginLeft: 'auto' }}>
                        <CopyBtn text={[
                          `${starStr} (${avgRaw.toFixed(1)}/5 avg)`,
                          ratingsLine,
                          t.feedback_text && `\n"${t.feedback_text}"\n— ${firstName}`,
                        ].filter(Boolean).join('\n')} label="Copy All" />
                      </div>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: C.terracotta, marginBottom: 16, letterSpacing: '0.02em' }}>
                      {ratingsLine}
                    </div>

                    {/* Shareable answer blocks — only the optional feedback text now */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[
                        { label: 'Feedback', value: t.feedback_text },
                      ].filter(a => a.value).map(a => (
                        <div key={a.label} style={{
                          background: '#fff',
                          border: `1.5px solid ${C.border}`,
                          borderRadius: 10,
                          padding: '14px 16px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                              {a.label}
                            </span>
                            <CopyBtn text={`${starStr} (${stars}/5)\n\n"${a.value}"\n— ${firstName}`} label="Copy" />
                          </div>
                          <p style={{ fontSize: '0.9rem', color: C.text, lineHeight: 1.65 }}>
                            "{a.value}"
                          </p>
                          <p style={{ fontSize: '0.82rem', color: C.terracotta, marginTop: 6, fontWeight: 600 }}>— {firstName}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
